export interface CustomLogicValidationResult {
	valid: boolean;
	error?: string;
}

export type CustomLogicTokenType = 'NUMBER' | 'AND' | 'OR' | 'NOT' | 'LPAREN' | 'RPAREN' | 'EOF';

const WHITESPACE_REGEX = /\s/u;
const NUMERIC_REGEX = /\d/u;
const LETTER_REGEX = /[A-Za-z]/u;

export interface CustomLogicToken {
	position: number;
	type: CustomLogicTokenType;
	value: string;
}

export function tokenizeCustomLogicString(input: string): CustomLogicToken[] | { error: string } {
	const tokens: CustomLogicToken[] = [];
	let i = 0;

	while (i < input.length) {
		const currentChar = input[ i ];

		if (WHITESPACE_REGEX.test(currentChar)) {
			i++;

			continue;
		}

		if (currentChar === '(') {
			tokens.push({
				position: i,
				type: 'LPAREN',
				value: '('
			});
			i++;

			continue;
		}

		if (currentChar === ')') {
			tokens.push({
				position: i,
				type: 'RPAREN',
				value: ')'
			});
			i++;

			continue;
		}

		if (NUMERIC_REGEX.test(currentChar)) {
			let numStr = '';
			const start = i;

			while (i < input.length && NUMERIC_REGEX.test(input[ i ])) {
				numStr += input[ i ];
				i++;
			}

			tokens.push({
				position: start,
				type: 'NUMBER',
				value: numStr
			});

			continue;
		}

		if (LETTER_REGEX.test(currentChar)) {
			let word = '';
			const start = i;

			while (i < input.length && LETTER_REGEX.test(input[ i ])) {
				word += input[ i ];
				i++;
			}

			const upper = word.toUpperCase();

			if (upper === 'AND' || upper === 'OR' || upper === 'NOT') {
				tokens.push({
					position: start,
					type: upper as 'AND' | 'OR' | 'NOT',
					value: upper
				});

				continue;
			} else {
				return { error: `Unrecognized keyword "${word}" at position ${start + 1}` };
			}
		}

		return { error: `Unexpected character "${currentChar}" at position ${i + 1}` };
	}

	tokens.push({
		position: input.length,
		type: 'EOF',
		value: ''
	});

	return tokens;
}

export type CustomLogicNode =
	| { kind: 'number'; position: number; value: number }
	| { kind: 'not'; operand: CustomLogicNode }
	| { kind: 'and'; left: CustomLogicNode; right: CustomLogicNode }
	| { kind: 'or'; left: CustomLogicNode; right: CustomLogicNode }
	| { kind: 'group'; inner: CustomLogicNode };

export function parseCustomLogicTokens(tokens: CustomLogicToken[]): CustomLogicNode | { error: string } {
	let pos = 0;

	const peek = () => tokens[ pos ];
	const consume = () => tokens[ pos++ ];

	// Declare so parsePrimary can call parseOr for grouped sub-expressions.
	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	const parseOr = (): CustomLogicNode | { error: string } => parseOrImpl();

	const parsePrimary = (): CustomLogicNode | { error: string } => {
		const token = peek();

		if (token.type === 'LPAREN') {
			consume();

			if (peek().type === 'RPAREN') {
				return { error: `Empty parentheses at position ${token.position + 1}` };
			}

			const inner = parseOr();

			if ('error' in inner) {
				return inner;
			}

			if (peek().type !== 'RPAREN') {
				return { error: `Missing closing ")" for "(" at position ${token.position + 1}` };
			}

			consume();

			return {
				inner,
				kind: 'group'
			};
		}

		if (token.type === 'NUMBER') {
			consume();

			return {
				kind: 'number',
				position: token.position,
				value: parseInt(token.value, 10)
			};
		}

		if (token.type === 'RPAREN') {
			return { error: `Unexpected ")" at position ${token.position + 1} — no matching "("` };
		}

		if (token.type === 'EOF') {
			return { error: 'Unexpected end of expression' };
		}

		// eslint-disable-next-line max-len
		return { error: `Unexpected "${token.value}" at position ${token.position + 1} — expected a rule number, NOT, or "("` };
	};

	// NOT calls parsePrimary (not parseUnary) to keep double-NOT invalid.
	const parseUnary = (): CustomLogicNode | { error: string } => {
		const token = peek();

		if (token.type === 'NOT') {
			consume();
			const next = peek();

			if (next.type === 'EOF' || next.type === 'AND' || next.type === 'OR' || next.type === 'NOT') {
				return { error: `NOT at position ${token.position + 1} must be followed by a rule number or "("` };
			}

			const operand = parsePrimary();

			if ('error' in operand) {
				return operand;
			}

			return {
				kind: 'not',
				operand
			};
		}

		return parsePrimary();
	};

	const parseAnd = (): CustomLogicNode | { error: string } => {
		let left = parseUnary();

		if ('error' in left) {
			return left;
		}

		while (peek().type === 'AND') {
			consume();

			if (peek().type === 'EOF') {
				return { error: 'Expression cannot end with an operator' };
			}

			const right = parseUnary();

			if ('error' in right) {
				return right;
			}

			left = {
				kind: 'and',
				left,
				right
			};
		}

		return left;
	};

	const parseOrImpl = (): CustomLogicNode | { error: string } => {
		let left = parseAnd();

		if ('error' in left) {
			return left;
		}

		while (peek().type === 'OR') {
			consume();

			if (peek().type === 'EOF') {
				return { error: 'Expression cannot end with an operator' };
			}

			const right = parseAnd();

			if ('error' in right) {
				return right;
			}

			left = {
				kind: 'or',
				left,
				right
			};
		}

		return left;
	};

	if (peek().type === 'EOF') {
		return { error: 'Expression cannot be empty' };
	}

	const result = parseOr();

	if ('error' in result) {
		return result;
	}

	const remaining = peek();

	if (remaining.type !== 'EOF') {
		if (remaining.type === 'RPAREN') {
			return { error: `Unexpected ")" at position ${remaining.position + 1} — no matching "("` };
		}

		return { error: `Unexpected "${remaining.value}" at position ${remaining.position + 1}` };
	}

	return result;
}

function validateNodeNumberRanges(node: CustomLogicNode, ruleCount: number): string | null {
	const { kind } = node;

	switch (kind) {
		case 'number':
			if (node.value < 1 || node.value > ruleCount) {
				// eslint-disable-next-line max-len
				return `Rule ${node.value} at position ${node.position + 1} is out of range — must be between 1 and ${ruleCount}`;
			}

			return null;
		case 'not':
			return validateNodeNumberRanges(node.operand, ruleCount);
		case 'and':
		case 'or':
			return validateNodeNumberRanges(node.left, ruleCount) ?? validateNodeNumberRanges(node.right, ruleCount);
		case 'group':
			return validateNodeNumberRanges(node.inner, ruleCount);
		default:
			return `Unexpected node kind: ${kind}`;
	}
}

export function validateCustomLogic(logic: string, ruleCount: number): CustomLogicValidationResult {
	if (ruleCount < 1) {
		return {
			error: 'At least one rule must exist to define custom logic',
			valid: false
		};
	}

	const tokensOrError = tokenizeCustomLogicString(logic.trim());

	if ('error' in tokensOrError) {
		return {
			error: tokensOrError.error,
			valid: false
		};
	}

	const customNode = parseCustomLogicTokens(tokensOrError);

	if ('error' in customNode) {
		return {
			error: customNode.error,
			valid: false
		};
	}

	const rangeError = validateNodeNumberRanges(customNode, ruleCount);

	if (rangeError) {
		return {
			error: rangeError,
			valid: false
		};
	}

	return { valid: true };
}

export type FilterDefBuilder = (invert: boolean) => string;

function buildDqlFromNode(
	node: CustomLogicNode,
	filterDefs: Map<number, FilterDefBuilder>,
	invert: boolean
): string {
	const { kind } = node;

	switch (kind) {
		case 'number': {
			const buildFn = filterDefs.get(node.value);

			return buildFn ? buildFn(invert) : '';
		}
		case 'not':
			return buildDqlFromNode(node.operand, filterDefs, !invert);
		case 'and': {
			const left = buildDqlFromNode(node.left, filterDefs, invert);
			const right = buildDqlFromNode(node.right, filterDefs, invert);
			const joiner = invert ? '|' : ' ';

			return left && right ? `${left}${joiner}${right}` : left || right;
		}
		case 'or': {
			const left = buildDqlFromNode(node.left, filterDefs, invert);
			const right = buildDqlFromNode(node.right, filterDefs, invert);
			const joiner = invert ? ' ' : '|';

			return left && right ? `${left}${joiner}${right}` : left || right;
		}
		case 'group': {
			const inner = buildDqlFromNode(node.inner, filterDefs, invert);

			return inner ? `(${inner})` : '';
		}
		default:
			return '';
	}
}

export function buildCustomLogicFilter(logic: string, filterDefs: Map<number, FilterDefBuilder>): string {
	const tokensOrError = tokenizeCustomLogicString(logic);

	if ('error' in tokensOrError) {
		return '';
	}

	const customNode = parseCustomLogicTokens(tokensOrError);

	if ('error' in customNode) {
		return '';
	}

	return buildDqlFromNode(customNode, filterDefs, false);
}
